import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, RotateCcw, MoreHorizontal, ListVideo, Filter, SlidersHorizontal, Download } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { LiveConsoleBreadcrumbs } from '../LiveConsoleBreadcrumbs';
import {
  DataTable, EmptyState, Pagination, StatusPill, FilterBuilder,
  type ColumnDef, type FilterField, type FilterCondition,
} from '@vnetwork/ui/data';
import {
  Button,
  IconButton,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  Badge,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import { useLiveStreams } from '@/api/media/live-live-streams';
import type { LiveStreamItem } from '@/api/media/live-live-streams-types';
import { streamStatusTone } from '../live-stream-status';
import { useJobActions, confirmProps } from './use-job-actions';
import styles from './CurrentJobsList.module.css';

interface Props {
  serviceId: string;
}

// Status options drive both the legacy URL param and the Filters popover.
const STATUS_OPTIONS = [
  { value: '1', label: 'To be started' },
  { value: '2', label: 'Streaming' },
  { value: '4', label: 'Forbidden' },
  { value: '7', label: 'Timed out' },
  { value: '10', label: 'Expired' },
  { value: '12', label: 'Interrupted' },
  { value: '6', label: 'Ended' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

// Columns the user can toggle via "Display options" (Stream Name + actions fixed).
const TOGGLEABLE = [
  { id: 'app_name', label: 'App Name' },
  { id: 'type', label: 'Type' },
  { id: 'created', label: 'Created' },
  { id: 'started', label: 'Started' },
  { id: 'status', label: 'Status' },
];
const DEFAULT_VISIBLE: Record<string, boolean> = {
  app_name: true, type: true, created: true, started: true, status: true,
};

const fmtTime = (value?: number | string | null): string => {
  if (!value) return '—';
  const ms = typeof value === 'string' ? Date.parse(value) : value;
  if (!ms || !Number.isFinite(ms)) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(new Date(ms)).replace(',', '');
};

/** Download the given jobs as a CSV file. */
function exportCsv(rows: LiveStreamItem[]) {
  const head = ['app_name', 'stream_name', 'type', 'created', 'started', 'status', 'updated'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows
    .map((r) =>
      [
        r.app_name,
        r.stream_name,
        r.stream_kind,
        fmtTime(r.provider_metadata?.created || r.created_at),
        fmtTime(r.provider_metadata?.started),
        r.status,
        fmtTime(r.updated_at),
      ]
        .map(esc)
        .join(','),
    )
    .join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `live-jobs-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function LiveCurrentJobs({ serviceId }: Props) {
  const navigate = useNavigate();
  const base = `/services/live/${serviceId}`;
  const [searchParams, setSearchParams] = useSearchParams();

  const { pendingAction, setPendingAction, handleConfirm, isBusy } = useJobActions(serviceId);

  const [page, setPage] = useState(() => Math.max(1, Number(searchParams.get('page') || '1')));
  const [pageSize, setPageSize] = useState(() => {
    const p = Number(searchParams.get('per_page') || String(DEFAULT_PAGE_SIZE));
    return PAGE_SIZE_OPTIONS.includes(p) ? p : DEFAULT_PAGE_SIZE;
  });
  // Status filter lives in the FilterBuilder popover; seed it from the URL param.
  const [conditions, setConditions] = useState<FilterCondition[]>(() => {
    const s = searchParams.get('status');
    return s && s !== '-1' ? [{ id: 'init-status', field: 'status', operator: 'equals', value: s }] : [];
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  const statusValue = conditions.find((c) => c.field === 'status' && c.value.trim())?.value;

  const filterFields: FilterField[] = [
    {
      value: 'status',
      label: 'Status',
      input: 'select',
      operators: [{ value: 'equals', label: 'is' }],
      options: STATUS_OPTIONS,
    },
  ];

  // Debounce search input — reset to page 1 on change
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => window.clearTimeout(t);
  }, [search]);

  // Sync filter state to URL params
  useEffect(() => {
    const params: Record<string, string> = {
      page: String(page),
      per_page: String(pageSize),
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (statusValue) params.status = statusValue;
    setSearchParams(params, { replace: true });
  }, [page, pageSize, debouncedSearch, statusValue, setSearchParams]);

  const { data, isLoading, isError, refetch } = useLiveStreams(
    serviceId,
    page,
    pageSize,
    debouncedSearch || undefined,
    statusValue,
    { refetchInterval: 10_000, refetchIntervalInBackground: false },
  );

  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const rows = data?.items ?? [];

  const columns = useMemo<ColumnDef<LiveStreamItem, unknown>[]>(() => {
    const all: Record<string, ColumnDef<LiveStreamItem, unknown>> = {
      app_name: {
        id: 'app_name',
        header: 'App Name',
        enableSorting: false,
        cell: ({ row }) => <span style={{ color: 'var(--vn-fg-default)' }}>{row.original.app_name || '—'}</span>,
        size: 150,
      },
      type: {
        id: 'type',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => {
          const kind = row.original.stream_kind || 'linear';
          return (
            <Badge tone="neutral" appearance="subtle" size="sm">
              {kind.toUpperCase()}
            </Badge>
          );
        },
        size: 90,
      },
      created: {
        id: 'created',
        header: 'Created',
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.cellMuted}>
            {fmtTime(row.original.provider_metadata?.created || row.original.created_at)}
          </span>
        ),
        size: 160,
      },
      started: {
        id: 'started',
        header: 'Started',
        enableSorting: false,
        cell: ({ row }) => (
          <span className={styles.cellMuted}>{fmtTime(row.original.provider_metadata?.started)}</span>
        ),
        size: 160,
      },
      status: {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }) => {
          const s = row.original;
          const updated = fmtTime(s.updated_at);
          return (
            <div className={styles.cellMeta}>
              <StatusPill tone={streamStatusTone(s.status)}>{s.status || '—'}</StatusPill>
              {updated !== '—' && <span className={styles.cellMetaSub}>Updated: {updated}</span>}
            </div>
          );
        },
        size: 180,
      },
    };
    const streamNameCol: ColumnDef<LiveStreamItem, unknown> = {
      id: 'stream_name',
      header: 'Stream Name',
      enableSorting: false,
      cell: ({ row }) => <span className={styles.streamName}>{row.original.stream_name || '—'}</span>,
      size: 160,
    };
    const actionsCol: ColumnDef<LiveStreamItem, unknown> = {
      id: 'actions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const r = row.original;
        // BE rejects reset unless local status is exactly "forbidden"; do not
        // gate on provider_status alone — that produces a 400 from the server.
        const isForbidden = r.status?.toLowerCase() === 'forbidden';
        return (
          <div className={styles.actionRow} onClick={(e) => e.stopPropagation()}>
            <Button
              variant="secondary"
              size="sm"
              aria-label={`Monitor stream ${r.stream_name}`}
              onClick={() => navigate(`${base}/current-jobs/${r.id}?tab=monitor`)}
            >
              Monitor
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <IconButton variant="ghost" size="sm" aria-label="Stream actions">
                  <MoreHorizontal size={15} />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => navigate(`${base}/current-jobs/${r.id}`)}>
                  View details
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {isForbidden ? (
                  <DropdownMenuItem onSelect={() => setPendingAction({ kind: 'resume', row: r })}>
                    Resume
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => setPendingAction({ kind: 'forbid', row: r })}>
                    Forbid
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem tone="danger" onSelect={() => setPendingAction({ kind: 'delete', row: r })}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      size: 120,
    };
    return [streamNameCol, ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => all[c.id]), actionsCol];
  }, [base, navigate, visible, setPendingAction]);

  const conf = pendingAction ? confirmProps(pendingAction) : null;

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<LiveConsoleBreadcrumbs trail={[{ label: 'Current Jobs' }]} />}
        title={`Current Jobs${total ? ` (${total})` : ''}`}
        description="Live stream jobs and their ingest status. Click a row to view details."
      />

      {isError && (
        <Alert tone="warning" title="Failed to load jobs">
          Could not retrieve live stream jobs. Check your connection and try again.
        </Alert>
      )}

      <Toolbar
        start={
          <Input
            placeholder="Search jobs…"
            startSlot={<Search size={15} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperClassName={styles.searchInput}
          />
        }
        end={
          <>
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" startIcon={<Filter size={15} />}>
                  Filters{conditions.length ? ` (${conditions.length})` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" style={{ padding: 0 }}>
                <FilterBuilder
                  fields={filterFields}
                  defaultValue={conditions.length ? conditions : undefined}
                  onApply={(c) => {
                    setConditions(c.filter((x) => x.value.trim()));
                    setPage(1);
                    setFilterOpen(false);
                  }}
                  onClose={() => setFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>
            {conditions.length > 0 && (
              <Button variant="ghost" onClick={() => { setConditions([]); setPage(1); }}>
                Clear
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
                {TOGGLEABLE.map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.id}
                    checked={visible[c.id]}
                    onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="secondary"
              startIcon={<Download size={15} />}
              disabled={rows.length === 0}
              onClick={() => exportCsv(rows)}
            >
              Export
            </Button>
            <Button
              variant="secondary"
              startIcon={<RotateCcw size={15} />}
              onClick={() => refetch()}
              aria-label="Refresh jobs list"
            >
              Refresh
            </Button>
          </>
        }
      />

      {isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={52} />
          ))}
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => r.id}
            onRowClick={(r) => navigate(`${base}/current-jobs/${r.id}`)}
            emptyState={
              total === 0 && !debouncedSearch && !statusValue ? (
                <EmptyState
                  icon={<ListVideo size={20} />}
                  title="No jobs found"
                  description="No live stream jobs have been created yet."
                />
              ) : debouncedSearch || statusValue ? (
                'No jobs match your search or filters.'
              ) : (
                'No jobs to display.'
              )
            }
            footer={total > 0 ? `${total} job${total !== 1 ? 's' : ''}` : undefined}
          />
          {(pageCount > 1 || PAGE_SIZE_OPTIONS.length > 1) && (
            <div className={styles.paginationRow}>
              <div className={styles.pageSizeSelect}>
                <label htmlFor="page-size-select" className={styles.pageSizeLabel}>Rows per page</label>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}
                >
                  <SelectTrigger id="page-size-select" size="sm" style={{ width: 72 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pageCount > 1 && (
                <Pagination
                  page={page}
                  pageCount={pageCount}
                  onPageChange={setPage}
                  summary={`Page ${page} of ${pageCount}`}
                />
              )}
            </div>
          )}
        </>
      )}

      {pendingAction && conf && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setPendingAction(null)}
          title={conf.title}
          description={conf.description}
          confirmLabel={conf.confirmLabel}
          destructive={conf.destructive}
          requireText={conf.requireText}
          loading={isBusy}
          onConfirm={handleConfirm}
        />
      )}
    </Container>
  );
}

export default LiveCurrentJobs;
